/**
 * responseStyleEngine.js
 *
 * Selects Kevia's conversational mode based on context signals and
 * returns the behavioral instruction string that gets injected into
 * the system prompt immediately before each LLM call.
 *
 * Modes:
 *   concise    — quick data query; max 2–3 sentences
 *   pastoral   — followup / person / family; 1–2 short paragraphs
 *   analytical — statistics / district overview; up to 3 paragraphs + optional list
 *   reflective — sermon / spiritual reflection; contemplative, no lists
 *   urgent     — critical pressure / hard deadline; max 2 sentences
 *   emotional  — grief / pastoral care; very gentle, max 1 paragraph
 */

export const CONV_MODES = {
  CONCISE:    "concise",
  PASTORAL:   "pastoral",
  ANALYTICAL: "analytical",
  REFLECTIVE: "reflective",
  URGENT:     "urgent",
  EMOTIONAL:  "emotional",
};

const MODE_INSTRUCTIONS = {
  concise:
    "MODO CONCISO: Responde en máximo 2–3 oraciones directas. Sin introducción, sin conclusión, sin relleno. Solo la información más útil.",
  pastoral:
    "MODO PASTORAL: 1–2 párrafos cortos. Responde la pregunta primero. Si los datos lo justifican, añade al final una sola observación pastoral útil. Detente ahí.",
  analytical:
    "MODO ANALÍTICO: Hasta 3 párrafos o una lista breve si organiza mejor la información. Sé preciso con los números. No generes texto sin datos reales de fondo.",
  reflective:
    "MODO REFLEXIVO: Hasta 3 párrafos. Tono contemplativo y sereno. Sin listas. Prioriza la resonancia espiritual sobre la exhaustividad informativa.",
  urgent:
    "MODO URGENTE: Máximo 2 oraciones. Lo más importante primero. Si hay una acción recomendada, exprésala en una línea.",
  emotional:
    "MODO EMOCIONAL: Máximo 1 párrafo, muy breve. Sin pasos de acción a menos que se pidan. Sin análisis. Solo presencia y acompañamiento.",
};

/**
 * Select the appropriate conversational mode based on runtime signals.
 *
 * @param {{ intent: string, mood: string, pressure: string|null, temporalIntent: string|null, workflowActive: boolean }} params
 * @returns {string} — one of CONV_MODES values
 */
export function selectConversationalMode({ intent, mood, pressure, temporalIntent, workflowActive }) {
  // Emotional situations take priority over everything else
  if (mood === "grief") return CONV_MODES.EMOTIONAL;

  // Urgency signals
  if (mood === "urgent" || pressure === "critical") return CONV_MODES.URGENT;
  if (pressure === "high" && intent !== "sermon" && intent !== "reflection") return CONV_MODES.URGENT;

  // Pastoral care context
  if (mood === "pastoral-care") return CONV_MODES.PASTORAL;

  // Content intent overrides
  if (intent === "sermon" || intent === "reflection") return CONV_MODES.REFLECTIVE;
  if (intent === "analysis")                          return CONV_MODES.ANALYTICAL;

  // Temporal precision → the pastor is asking something quick and specific
  if (temporalIntent === "today" || temporalIntent === "next")             return CONV_MODES.CONCISE;
  if (temporalIntent === "week"  && intent === "planning")                 return CONV_MODES.CONCISE;

  // Active workflow → the pastor is in the middle of a task; keep it focused
  if (workflowActive && intent === "planning") return CONV_MODES.PASTORAL;

  return CONV_MODES.PASTORAL;
}

/**
 * Return the behavioral instruction string for a given mode.
 *
 * @param {string} mode
 * @returns {string}
 */
export function getModeInstructions(mode) {
  return MODE_INSTRUCTIONS[mode] ?? MODE_INSTRUCTIONS[CONV_MODES.PASTORAL];
}
